import { createAsyncThunk } from '@reduxjs/toolkit';

import { registrarAcao } from '@/shared/utils/reportAction';
import type { AppDispatch, RootState } from '@/store';

import type {
  Config,
  ConfiguracaoDoAdmin,
  DesfechoDoInicio,
  ModoDaSimulacao,
  MotivoDeInicio,
} from '../../types';
import { corpoDoInicioDoAdmin, modoDaConfiguracao } from '../../utils/execucaoDoAdmin';
import { intencaoReaproveitavel } from '../../utils/intencaoDeComando';
import {
  buscarSimulacaoAtual,
  conferirFormularioDoInicio,
  encerrarSimulacao,
  recuperarSimulacaoEmCriacao,
} from '../ciclo/actions';
import {
  contextoDoAdminAberto,
  escritorioDoAdminTrocado,
  intencaoRegistrada,
  modoDoAdminEscolhido,
} from '../eventosDoStore';
import { paraErroDaSimulacao, registrarFalha } from '../falhasDaSimulacao';
import { selectEstadoDaSimulacao as estadoDaFeature } from '../selectors';
import { servicosDaSimulacao } from '../servicosDaSimulacao';
import { simulacaoAdminService } from '../services';

/** A configuração mudou entre a leitura da tela e o Iniciar: a tela relê e mantém os dados (CA-14). */
const CONFLITOS_DO_INICIO = new Set([
  'MODO_INDISPONIVEL',
  'REVISAO_DO_RASCUNHO_ALTERADA',
  'PUBLICACAO_ALTERADA',
]);

export const buscarConfiguracaoDoAdmin = createAsyncThunk<
  ConfiguracaoDoAdmin,
  { flowId: string; escritorioId: string },
  Config
>(
  'negociacaoSimulacao/buscarConfiguracaoDoAdmin',
  async ({ flowId, escritorioId }, { rejectWithValue }) => {
    try {
      return await simulacaoAdminService.buscarConfiguracao(flowId, escritorioId);
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      registrarFalha('buscarConfiguracaoDoAdmin', erro, falha, { flowId });
      return rejectWithValue(falha);
    }
  },
  { condition: (_, { getState }) => estadoDaFeature(getState()).superficie === 'ADMIN' },
);

/** A configuração do escritório e do fluxo da tela, nesta ordem, e depois a atual deles. */
export const carregarContextoDoAdmin =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { contextoAdmin } = estadoDaFeature(getState());
    if (!contextoAdmin?.escritorioId) return;
    const { flowId, escritorioId } = contextoAdmin;
    // A identidade do agente vem da configuração; o principal salvo pode diferir do executado.
    await dispatch(buscarConfiguracaoDoAdmin({ flowId, escritorioId }));
    await dispatch(buscarSimulacaoAtual());
  };

export const abrirSimulacaoDoAdmin =
  ({ flowId, escritorioId }: { flowId: string; escritorioId: string | null }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { superficie, contextoAdmin } = estadoDaFeature(getState());
    if (
      superficie === 'ADMIN' &&
      contextoAdmin?.flowId === flowId &&
      contextoAdmin.escritorioId === escritorioId
    )
      return;
    dispatch(contextoDoAdminAberto({ flowId, escritorioId }));
  };

/** O contexto mudou com a simulação ATIVA: ela é encerrada, como na troca de agente do cliente. */
const encerrarAtivaPorAlteracao = (dispatch: AppDispatch, getState: () => RootState) => {
  const { simulacao } = estadoDaFeature(getState());
  if (simulacao?.status !== 'ATIVA') return;
  void dispatch(encerrarSimulacao({ simulacaoId: simulacao.id, motivo: 'DADOS_ALTERADOS' }));
};

/**
 * Outro escritório é outro contexto (CA-2): a simulação ativa sai encerrada, e nada da anterior
 * (conversa, configuração, logs, respostas em voo) chega à tela do novo escritório.
 */
export const trocarEscritorioDaSimulacaoDoAdmin =
  (escritorioId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    const { contextoAdmin } = estadoDaFeature(getState());
    if (!contextoAdmin || contextoAdmin.escritorioId === escritorioId) return;
    encerrarAtivaPorAlteracao(dispatch, getState);
    dispatch(escritorioDoAdminTrocado(escritorioId));
  };

/**
 * Trocar o modo exige nova execução e não muda a conversa anterior, que fica no core encerrada
 * (CA-16). Os campos de início são os do agente do modo, e os valores do outro não passam.
 */
export const escolherModoDaSimulacaoDoAdmin =
  (modo: ModoDaSimulacao) => (dispatch: AppDispatch, getState: () => RootState) => {
    const { contextoAdmin } = estadoDaFeature(getState());
    if (!contextoAdmin || contextoAdmin.modo === modo) return;
    encerrarAtivaPorAlteracao(dispatch, getState);
    dispatch(modoDoAdminEscolhido(modo));
  };

const podeIniciarNoAdmin = (state: RootState): boolean => {
  const { superficie, iniciando, contextoAdmin, configuracaoAdmin } = estadoDaFeature(state);
  if (superficie !== 'ADMIN' || iniciando || !contextoAdmin?.modo || !contextoAdmin.escritorioId)
    return false;
  const configuracao = configuracaoAdmin.dados;
  return (
    configuracao !== null &&
    configuracao.escritorioId === contextoAdmin.escritorioId &&
    modoDaConfiguracao(configuracao, contextoAdmin.modo).disponivel
  );
};

/**
 * O início do admin, com o modo e a revisão ou versão que a tela leu. Repetir com o mesmo corpo
 * depois de um desfecho desconhecido reaproveita a chave, como no cliente (CA-9).
 */
export const iniciarSimulacaoDoAdmin = createAsyncThunk<
  DesfechoDoInicio,
  { motivo: MotivoDeInicio },
  Config
>(
  'negociacaoSimulacao/iniciarDoAdmin',
  async ({ motivo }, { dispatch, getState, rejectWithValue }) => {
    const {
      formulario,
      intencoes,
      versaoDoFormulario,
      escritorioId,
      contextoAdmin,
      configuracaoAdmin,
    } = estadoDaFeature(getState());
    const dados = corpoDoInicioDoAdmin(
      contextoAdmin,
      configuracaoAdmin.dados,
      formulario.valores,
      motivo,
    );
    if (!dados) return rejectWithValue(paraErroDaSimulacao(null));
    const flowId = formulario.flowId ?? dados.flowId;
    const inicio = { versao: versaoDoFormulario, flowId, escritorioId };
    const intencao = intencaoReaproveitavel(intencoes, 'iniciar', dados);
    const servicos = servicosDaSimulacao(getState());
    dispatch(intencaoRegistrada({ tipo: 'iniciar', intencao }));
    try {
      const estado = await simulacaoAdminService.iniciar(dados, intencao.chave);
      registrarAcao('negociacaoSimulacao.iniciada', {
        simulacaoId: estado.id,
        flowId,
        motivo,
        superficie: 'ADMIN',
        modo: dados.modo,
        escritorioId: dados.escritorioId,
      });
      return conferirFormularioDoInicio(estado, inicio, dispatch, getState);
    } catch (erro) {
      const falha = paraErroDaSimulacao(erro);
      if (estadoDaFeature(getState()).escritorioId !== escritorioId) return rejectWithValue(falha);
      const emCriacao = await recuperarSimulacaoEmCriacao(falha, servicos.buscarAtual);
      if (emCriacao) return conferirFormularioDoInicio(emCriacao, inicio, dispatch, getState);
      if (falha.codigo && CONFLITOS_DO_INICIO.has(falha.codigo)) {
        void dispatch(
          buscarConfiguracaoDoAdmin({ flowId: dados.flowId, escritorioId: dados.escritorioId }),
        );
      }
      registrarFalha('iniciarDoAdmin', erro, falha, { flowId });
      return rejectWithValue(falha);
    }
  },
  { condition: (_, { getState }) => podeIniciarNoAdmin(getState()) },
);

/**
 * Reiniciar relê a configuração antes de iniciar: a execução nova usa a revisão ou a versão de
 * agora, no mesmo modo, e a faixa mostra a que ela usou (CA-16).
 */
export const reiniciarSimulacaoDoAdmin =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { contextoAdmin, iniciando } = estadoDaFeature(getState());
    if (!contextoAdmin?.escritorioId || iniciando) return;
    const { flowId, escritorioId } = contextoAdmin;
    const leitura = await dispatch(buscarConfiguracaoDoAdmin({ flowId, escritorioId }));
    if (!buscarConfiguracaoDoAdmin.fulfilled.match(leitura)) return;
    await dispatch(iniciarSimulacaoDoAdmin({ motivo: 'REINICIO' }));
  };
